import { createContext, FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { TApiErrorResponse, EErrorResponseType } from '../api/types';
import { EQueryStringParam, EThirdPartyProvider } from '../util/constants';

export enum EErrorUiState {
    default,
    generic,
    thirdPartyGeneric,
    thirdPartyConnected,
    noCorporateUserFound,
    corporateUserConflict,
    corporateUserNoPermission,
}

export type TErrorContext = {
    errorUiState: EErrorUiState;
    connectedProvider: EThirdPartyProvider;
};

const thirdPartyConnectedWith = (error: TApiErrorResponse): string => {
    let connectedProvider = '';
    const parsedError = error.detail.split(':');
    parsedError.length > 0 && (connectedProvider = parsedError[1]);
    return connectedProvider;
};

const mapThirdPartyProvider = (provider: string): EThirdPartyProvider => {
    switch (provider) {
        case EThirdPartyProvider.facebook:
            return EThirdPartyProvider.facebook;
        case EThirdPartyProvider.google:
            return EThirdPartyProvider.google;
        case EThirdPartyProvider.apple:
            return EThirdPartyProvider.apple;
    }
};

const ErrorContext = createContext<TErrorContext | undefined>(undefined);

export const ErrorProvider: FC = (props) => {
    const [errorUiState, setErrorUiState] = useState(EErrorUiState.default);
    const [connectedProvider, setConnectedProvider] = useState<EThirdPartyProvider | null>(null);
    const { children } = props;
    const { search: queryParams } = useLocation();

    const parseError = () => {
        const errorParam = new URLSearchParams(queryParams).get(EQueryStringParam.error);
        let errorState = EErrorUiState.generic;
        if (errorParam) {
            try {
                const errorObject: TApiErrorResponse = JSON.parse(atob(errorParam));
                switch (errorObject.type) {
                    case EErrorResponseType.thirdParty:
                        const connectedProvider = thirdPartyConnectedWith(errorObject);
                        if (connectedProvider) {
                            errorState = EErrorUiState.thirdPartyConnected;
                            setConnectedProvider(mapThirdPartyProvider(connectedProvider));
                        } else {
                            errorState = EErrorUiState.thirdPartyGeneric;
                        }
                        break;
                    case EErrorResponseType.noCorporateUserFound:
                        errorState = EErrorUiState.noCorporateUserFound;
                        break;
                    case EErrorResponseType.corporateUserConflict:
                        errorState = EErrorUiState.corporateUserConflict;
                        break;
                    case EErrorResponseType.corporateUserNoPermission:
                        errorState = EErrorUiState.corporateUserNoPermission;
                        break;
                    default:
                        errorState = EErrorUiState.generic;
                        break;
                }
            } catch {
                console.error('Failed to parse error parameter');
            }
        }
        setErrorUiState(errorState);
    };

    useEffect(() => {
        parseError();
    }, []);

    return <ErrorContext.Provider value={{ errorUiState, connectedProvider }}>{children}</ErrorContext.Provider>;
};

export default ErrorContext;
